import { FormEvent, useState } from "react";
import { LoaderCircle, Plus, X } from "lucide-react";

import {
  createEmployee,
  createRegion,
  createRegionCity,
  createTeam,
  updateEmployee,
  updateRegion,
  updateTeam,
} from "./api";
import type { Employee, Region, Team } from "./types";

type Selection = { kind: "teams" | "employees" | "regions"; record?: Employee | Region | Team };

export function WorkforceEditor({
  selection,
  regions,
  canWrite,
  onClose,
  onSaved,
}: {
  selection: Selection;
  regions: Region[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const title = selection.record
    ? `Edit ${singular(selection.kind)}`
    : `Add ${singular(selection.kind)}`;
  return (
    <div className="client-drawer-backdrop" onMouseDown={onClose}>
      <section
        className="client-drawer workforce-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow lime">Organization record</span>
            <h2>{title}</h2>
          </div>
          <button className="icon-button" aria-label="Close organization record" onClick={onClose}>
            <X />
          </button>
        </header>
        <div className="drawer-body">
          {selection.kind === "employees" ? (
            <EmployeeForm
              employee={selection.record as Employee | undefined}
              regions={regions}
              canWrite={canWrite}
              onClose={onClose}
              onSaved={onSaved}
            />
          ) : null}
          {selection.kind === "teams" ? (
            <TeamForm
              team={selection.record as Team | undefined}
              regions={regions}
              canWrite={canWrite}
              onClose={onClose}
              onSaved={onSaved}
            />
          ) : null}
          {selection.kind === "regions" ? (
            <RegionForm
              region={selection.record as Region | undefined}
              canWrite={canWrite}
              onClose={onClose}
              onSaved={onSaved}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}

function EmployeeForm({
  employee,
  regions,
  canWrite,
  onClose,
  onSaved,
}: {
  employee?: Employee;
  regions: Region[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input = {
      name: String(fields.get("name")),
      email: String(fields.get("email")),
      phone: String(fields.get("phone")),
      status: String(fields.get("status")) as "ACTIVE" | "INACTIVE",
      capabilities: parseCapabilities(fields.get("capabilities")),
      defaultRegionId: nullable(fields.get("defaultRegionId")),
      notes: nullable(fields.get("notes")),
    };
    try {
      await (employee ? updateEmployee(employee.id, input) : createEmployee(input));
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }
  return (
    <form className="record-form" onSubmit={submit}>
      <div className="form-grid">
        <label className="wide-field">
          Full name
          <input name="name" defaultValue={employee?.name} maxLength={120} required />
        </label>
        <label>
          Email
          <input
            name="email"
            type="email"
            defaultValue={employee?.email}
            maxLength={254}
            required
          />
        </label>
        <label>
          Phone
          <input name="phone" type="tel" defaultValue={employee?.phone} maxLength={30} required />
        </label>
        <label>
          Status
          <select name="status" defaultValue={employee?.status ?? "ACTIVE"}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </label>
        <RegionSelect regions={regions} value={employee?.defaultRegionId} />
        <label className="wide-field">
          Capabilities
          <input
            name="capabilities"
            defaultValue={employee?.capabilities.join(", ")}
            placeholder="Separate capabilities with commas"
          />
        </label>
        <label className="wide-field">
          Private notes
          <textarea name="notes" rows={4} defaultValue={employee?.notes ?? ""} maxLength={10000} />
        </label>
      </div>
      <FormEnding
        failed={failed}
        sending={sending}
        canWrite={canWrite}
        onClose={onClose}
        label="Save employee"
      />
    </form>
  );
}

function TeamForm({
  team,
  regions,
  canWrite,
  onClose,
  onSaved,
}: {
  team?: Team;
  regions: Region[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input = {
      name: String(fields.get("name")),
      active: fields.get("active") === "on",
      defaultRegionId: nullable(fields.get("defaultRegionId")),
      notes: nullable(fields.get("notes")),
    };
    try {
      await (team ? updateTeam(team.id, input) : createTeam(input));
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }
  return (
    <form className="record-form" onSubmit={submit}>
      <div className="form-grid">
        <label className="wide-field">
          Team name
          <input name="name" defaultValue={team?.name} maxLength={120} required />
        </label>
        <RegionSelect regions={regions} value={team?.defaultRegionId} />
        <label className="toggle-field workforce-toggle">
          <input name="active" type="checkbox" defaultChecked={team?.active ?? true} /> Active team
        </label>
        <label className="wide-field">
          Private notes
          <textarea name="notes" rows={4} defaultValue={team?.notes ?? ""} maxLength={10000} />
        </label>
      </div>
      <FormEnding
        failed={failed}
        sending={sending}
        canWrite={canWrite}
        onClose={onClose}
        label="Save team"
      />
    </form>
  );
}

function RegionForm({
  region,
  canWrite,
  onClose,
  onSaved,
}: {
  region?: Region;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [addingCity, setAddingCity] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input = {
      name: String(fields.get("name")),
      description: nullable(fields.get("description")),
      active: fields.get("active") === "on",
    };
    try {
      await (region ? updateRegion(region.id, input) : createRegion(input));
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }
  return (
    <>
      <form className="record-form" onSubmit={submit}>
        <div className="form-grid">
          <label className="wide-field">
            Region name
            <input name="name" defaultValue={region?.name} maxLength={120} required />
          </label>
          <label className="wide-field">
            Description
            <textarea
              name="description"
              rows={3}
              defaultValue={region?.description ?? ""}
              maxLength={4000}
            />
          </label>
          <label className="toggle-field workforce-toggle">
            <input name="active" type="checkbox" defaultChecked={region?.active ?? true} /> Active
            region
          </label>
        </div>
        <FormEnding
          failed={failed}
          sending={sending}
          canWrite={canWrite}
          onClose={onClose}
          label="Save region"
        />
      </form>
      {region ? (
        <section className="region-cities">
          <div className="property-heading">
            <div>
              <span className="eyebrow">Coverage entries</span>
              <h3>{region.cities.length || "No"} configured cities</h3>
            </div>
            {canWrite ? (
              <button className="secondary-button small-action" onClick={() => setAddingCity(true)}>
                <Plus /> Add city
              </button>
            ) : null}
          </div>
          <div className="city-list">
            {region.cities.map((city) => (
              <span key={city.id}>
                {city.city}, {city.state}
              </span>
            ))}
          </div>
          {addingCity ? (
            <CityForm
              regionId={region.id}
              onCancel={() => setAddingCity(false)}
              onSaved={onSaved}
            />
          ) : null}
        </section>
      ) : null}
    </>
  );
}

function CityForm({
  regionId,
  onCancel,
  onSaved,
}: {
  regionId: string;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    try {
      await createRegionCity(regionId, {
        city: String(fields.get("city")),
        state: String(fields.get("state")),
        zipPattern: nullable(fields.get("zipPattern")),
      });
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }
  return (
    <form className="record-form city-form" onSubmit={submit}>
      <div className="form-grid">
        <label>
          City
          <input name="city" required maxLength={120} />
        </label>
        <label>
          State
          <input name="state" defaultValue="MA" required maxLength={2} />
        </label>
        <label className="wide-field">
          Optional ZIP pattern
          <input name="zipPattern" maxLength={32} />
        </label>
      </div>
      {failed ? <p className="form-error">We couldn’t save this city.</p> : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending}>
          {sending ? <LoaderCircle className="spin" /> : null} Save city
        </button>
      </div>
    </form>
  );
}

function RegionSelect({ regions, value }: { regions: Region[]; value?: string | null }) {
  return (
    <label>
      Default region
      <select name="defaultRegionId" defaultValue={value ?? ""}>
        <option value="">No default region</option>
        {regions
          .filter((region) => region.active || region.id === value)
          .map((region) => (
            <option key={region.id} value={region.id}>
              {region.name}
            </option>
          ))}
      </select>
    </label>
  );
}
function FormEnding({
  failed,
  sending,
  canWrite,
  onClose,
  label,
}: {
  failed: boolean;
  sending: boolean;
  canWrite: boolean;
  onClose: () => void;
  label: string;
}) {
  return (
    <>
      {failed ? (
        <p className="form-error" role="alert">
          We couldn’t save this record. Check for duplicate or invalid information.
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onClose}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending || !canWrite}>
          {sending ? <LoaderCircle className="spin" /> : null} {label}
        </button>
      </div>
    </>
  );
}
function parseCapabilities(value: FormDataEntryValue | null) {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}
function singular(value: Selection["kind"]) {
  return value === "employees" ? "employee" : value === "regions" ? "region" : "team";
}
